import {
  Switch as AriaSwitch,
  SwitchProps as AriaSwitchProps,
  Text,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { composeTailwindRenderProps, focusRing } from 'src/utils';

import { forwardRef, ReactNode } from 'react';

export interface SwitchProps extends Omit<AriaSwitchProps, 'children'> {
  children: ReactNode;
  showText?: boolean;
}

const track = tv({
  extend: focusRing,
  base: 'relative flex h-8 w-14 px-0.5 items-center shrink-0 cursor-pointer rounded-full transition duration-200 ease-in-out',
  variants: {
    isSelected: {
      false: 'bg-surface-alt-60 group-pressed:bg-surface-alt-60',
      true: 'bg-green group-pressed:bg-green-10',
    },
    isDisabled: {
      true: 'cursor-not-allowed',
    },
  },
});

const handle = tv({
  base: 'h-6.5 w-6.5 transform rounded-full bg-surface-alt outline outline-1 -outline-offset-1 outline-transparent shadow transition duration-200 ease-in-out',
  variants: {
    isSelected: {
      false: 'translate-x-[0%]',
      true: 'translate-x-[100%]',
    },
    isDisabled: {
      true: 'cursor-not-allowed',
    },
  },
});

const Switch = forwardRef<HTMLLabelElement, SwitchProps>(
  ({ children, showText = false, ...props }, ref) => (
    <AriaSwitch
      {...props}
      ref={ref}
      className={composeTailwindRenderProps(
        props.className,
        'group flex gap-2 items-center text-surface-alt-75 disabled:text-surface-alt-60 text-sm font-semibold transition'
      )}
    >
      {(renderProps) => (
        <>
          {children}
          <div className={track(renderProps)}>
            {renderProps.isSelected && showText && (
              <Text
                aria-hidden="true"
                className="text-xs absolute left-1.5 text-surface-alt"
              >
                Yes
              </Text>
            )}
            <span className={handle(renderProps)} />
            {!renderProps.isSelected && showText && (
              <Text
                aria-hidden="true"
                className="text-xs absolute right-1.5 text-surface-alt"
              >
                No
              </Text>
            )}
          </div>
        </>
      )}
    </AriaSwitch>
  )
);

Switch.displayName = 'Switch';

export default Switch;
